/**
 * /pro/claim — ONE-TAP landing for a gifted Pro invite.
 *
 * Mirrors the ambassador one-step claim: the token in the URL IS the
 * credential (emailed to the recipient, 30-day TTL), so this page never
 * redirects to /login. GET /pro/claim/info previews the gift; the
 * Activate button POSTs /pro/claim/complete which creates-or-matches
 * the account, stacks N months onto pro_until, sets the session
 * cookie, and drops them into the app already signed in.
 *
 * Fully localised via `proClaim.*` keys (en/es/fr/pt).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, Sparkles, Gift, ArrowRight, Mail } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { API, getProClaimInfo, completeProClaim } from "../lib/api";

// Shared card frame for the form and every gift state.
const Shell = ({ children }) => (
  <div
    className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-5 py-10"
    data-testid="pro-claim-page"
  >
    <div className="w-full max-w-md bg-white border border-[#EADDD7] rounded-3xl p-7 shadow-[0_20px_50px_-18px_rgba(155,106,108,0.20)]">
      <div className="flex items-center gap-2.5 mb-2">
        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center">
          <Sparkles className="h-4 w-4 text-white" />
        </div>
        <span className="font-serif text-xl tracking-tight text-[#2C2123]">
          TruBeauté
        </span>
      </div>
      {children}
    </div>
  </div>
);

const ProClaim = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null); // null = loading
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [tosVersion, setTosVersion] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getProClaimInfo(token);
        if (cancelled) return;
        setInfo(data);
        if (data.status === "valid" && data.name) setDisplayName(data.name);
      } catch {
        if (!cancelled) setInfo({ status: "invalid" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Same public ToS-version fetch Login.jsx uses.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/legal/tos-version`);
        if (!cancelled) setTosVersion(data?.current_version || null);
      } catch {
        if (!cancelled) setTosVersion("unknown");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-6">
        <p className="text-[#5C4B51]" data-testid="pro-claim-missing-token">
          {t("proClaim.missingToken")}
        </p>
      </div>
    );
  }
  if (info === null) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#9B6A6C]" />
      </div>
    );
  }
  if (info.status === "invalid" || info.status === "expired") {
    return (
      <Shell>
        <p
          className="text-[#5C4B51] mt-3"
          data-testid={`pro-claim-${info.status}`}
        >
          {t(
            info.status === "expired"
              ? "proClaim.errorExpired"
              : "proClaim.errorInvalid",
          )}
        </p>
        <p className="text-[11px] text-[#8A7D82] mt-5">
          <Link to="/" className="underline">
            {t("proClaim.backCta")}
          </Link>
        </p>
      </Shell>
    );
  }
  if (info.status === "claimed") {
    return (
      <Shell>
        <h1
          className="font-serif italic text-2xl text-[#2C2123] leading-tight mt-2 mb-2"
          data-testid="pro-claim-already-claimed"
        >
          {t("proClaim.alreadyClaimedHeadline")}
        </h1>
        <p className="text-sm text-[#5C4B51] mb-5">
          {t("proClaim.alreadyClaimedBody")}
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 bg-[#9B6A6C] text-white px-5 py-2.5 rounded-full hover:bg-[#805557] transition-colors font-medium text-sm"
          data-testid="pro-claim-signin-cta"
        >
          {t("proClaim.signInCta")}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </Shell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      const res = await completeProClaim({
        token,
        display_name: displayName.trim() || null,
        password: info.account_exists ? undefined : password || undefined,
        tos_version: tosVersion || "unknown",
      });
      setUser(res.user);
      toast.success(t("proClaim.success"));
      navigate("/my-routines", { replace: true });
    } catch (e2) {
      const code = e2?.response?.data?.detail;
      const errorKeys = {
        invalid_token: "proClaim.errorInvalid",
        expired: "proClaim.errorExpired",
        already_claimed: "proClaim.alreadyClaimedHeadline",
        weak_password: "proClaim.errorWeakPassword",
      };
      setErr(errorKeys[code] ? t(errorKeys[code]) : t("proClaim.errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell>
      <h1
        className="font-serif italic text-3xl text-[#2C2123] leading-tight mb-1"
        data-testid="pro-claim-headline"
      >
        {t("proClaim.headline")}
      </h1>
      <p className="text-sm text-[#5C4B51] mb-3" data-testid="pro-claim-subline">
        {t("proClaim.subline", { months: info.months })}
      </p>

      <p
        className="inline-flex items-center gap-1.5 text-xs text-[#5D4050] bg-[#FBF4EF] border border-[#EADDD7] rounded-full px-3 py-1.5 mb-5"
        data-testid="pro-claim-email"
      >
        <Mail className="h-3.5 w-3.5 text-[#9B6A6C]" />
        {t("proClaim.claimingAs", { email: info.email })}
      </p>

      {info.account_exists && (
        <p
          className="text-xs text-[#5C4B51] bg-[#F4EDE6] rounded-lg p-3 mb-4"
          data-testid="pro-claim-existing-note"
        >
          {t("proClaim.existingAccountNote")}
        </p>
      )}

      <form onSubmit={submit} className="space-y-4">
        {!info.account_exists && (
          <>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C4B51]">
                {t("proClaim.displayNameLabel")}
              </label>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#EADDD7] bg-[#FAF5F1] focus:border-[#9B6A6C] outline-none"
                placeholder="Alex Rivera"
                data-testid="pro-claim-display-name"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C4B51]">
                {t("proClaim.passwordLabel")}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                autoComplete="new-password"
                className="mt-1 w-full px-3 py-2.5 rounded-lg border border-[#EADDD7] bg-[#FAF5F1] focus:border-[#9B6A6C] outline-none"
                placeholder="••••••••"
                data-testid="pro-claim-password"
              />
              <p className="text-[11px] text-[#8A7D82] mt-1">
                {t("proClaim.passwordHelp")}
              </p>
            </div>
          </>
        )}

        {err && (
          <div
            className="text-sm text-[#9B6A6C] bg-[#FBF4EF] border border-[#EADDD7] rounded-lg p-3"
            data-testid="pro-claim-error"
          >
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white px-5 py-3 rounded-full hover:bg-[#805557] disabled:opacity-50 transition-colors font-medium"
          data-testid="pro-claim-submit"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Gift className="h-4 w-4" />
          )}
          {submitting ? t("proClaim.submitting") : t("proClaim.submit")}
          <ArrowRight className="h-4 w-4" />
        </button>
      </form>

      <p className="text-[11px] text-[#8A7D82] mt-4" data-testid="pro-claim-tos-note">
        <Trans
          i18nKey="proClaim.tosNote"
          components={[
            <Link to="/terms" className="underline" />,
            <Link to="/privacy" className="underline" />,
          ]}
        />
      </p>

      <p className="text-[11px] text-[#8A7D82] mt-3">
        <Link to="/" className="underline">
          {t("proClaim.backCta")}
        </Link>
      </p>
    </Shell>
  );
};

export default ProClaim;
